import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { volApi, getStoredVolProfile, clearVolSession, isVolLoggedIn } from "./volApi";
import { HARBORWELL_LOGO } from "@/lib/api";
import { Calendar, Clock, LogOut, HandHeart, Home, BookOpen } from "lucide-react";

export default function VolunteerLayout() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(getStoredVolProfile());

  useEffect(() => {
    if (!isVolLoggedIn()) return;
    // Re-fetch profile to keep status + permissions fresh
    volApi.get("/volunteers/me").then(({ data }) => setProfile(data)).catch(() => {});
  }, []);

  if (!isVolLoggedIn()) return <Navigate to="/volunteer/login" replace />;

  const handleSignOut = () => {
    clearVolSession();
    navigate("/volunteer/login");
  };

  const perms = new Set(profile?.permissions || []);
  // While the /volunteers/me round-trip is in flight after sign-in we may
  // not yet have the permissions list. Fall back to "show everything" so
  // we don't briefly hide tabs the volunteer is allowed to see — the page
  // re-renders the moment the perms arrive.
  const permsKnown = Array.isArray(profile?.permissions);
  const can = (key) => !permsKnown || perms.has(key);

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="volunteer-portal-layout">
      <header className="bg-white border-b border-[#E5E5E0] sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <NavLink to="/volunteer" className="flex items-center gap-2.5">
            <img src={HARBORWELL_LOGO} alt="HarborWell" className="h-9" />
            <div>
              <div className="font-display font-bold text-[#1E3A8A] text-sm leading-none">HarborWell</div>
              <div className="text-[9px] tracking-[0.2em] uppercase text-[#1E3A8A] mt-0.5">Volunteer</div>
            </div>
          </NavLink>
          <div className="flex items-center gap-2 text-sm">
            <span className="hidden sm:inline text-[#334155] truncate max-w-[180px]" data-testid="volunteer-portal-name">
              {profile?.name}
            </span>
            <button
              onClick={handleSignOut}
              data-testid="volunteer-portal-signout"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#E5E5E0] text-[#334155] hover:bg-[#F9F8F6]"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
        <nav className="max-w-5xl mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto border-t border-[#F0EFEB]" data-testid="volunteer-portal-nav">
          <Tab to="/volunteer" exact label="Home" icon={Home} testId="vol-tab-home" />
          {can("volunteer.events.view") && (
            <Tab to="/volunteer/events" label="Upcoming events" icon={Calendar} testId="vol-tab-events" />
          )}
          {can("volunteer.resources.view") && (
            <Tab to="/volunteer/resources" label="Resource directory" icon={BookOpen} testId="vol-tab-resources" />
          )}
          {can("volunteer.hours.log") && (
            <Tab to="/volunteer/hours" label="My hours" icon={Clock} testId="vol-tab-hours" />
          )}
        </nav>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 pb-24">
        <Outlet context={{ profile, can }} />
      </main>
      <footer className="text-center py-6 text-xs text-[#1E3A8A] flex items-center justify-center gap-1.5">
        <HandHeart className="w-3.5 h-3.5" /> Thank you for serving Eastern North Carolina.
      </footer>
    </div>
  );
}

const Tab = ({ to, label, icon: Icon, testId, exact }) => (
  <NavLink
    to={to}
    end={exact}
    data-testid={testId}
    className={({ isActive }) =>
      `inline-flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
        isActive
          ? "text-[#1E3A8A] border-[#1E3A8A]"
          : "text-[#334155] border-transparent hover:text-[#1E3A8A]"
      }`
    }
  >
    <Icon className="w-4 h-4" strokeWidth={1.75} />
    {label}
  </NavLink>
);
